<template>
  <div class="agent-detail-container">
    <div class="content-area">
      <!-- Page Header Area -->
      <div class="page-header">
        <div class="left-section">
          <v-btn
            color="grey-lighten-2"
            variant="flat"
            size="small"
            elevation="0"
            icon="mdi-arrow-left"
            @click="goBack"
            class="mr-3"/>
          <h1 class="page-title">
            {{ agent.name }}
          </h1>
        </div>

        <div class="right-section">
          <v-btn
            icon
            size="small"
            variant="text"
            @click="deleteAgent"
            class="call-btn mank-btn-icon mr-4 mank-btn-icon-md"
            color="primary">
            <Icon name="trash" variant="solid" color="default" size="16"/>
          </v-btn>
          <v-btn
            icon
            size="small"
            variant="text"
            @click="editAgent"
            class="call-btn mank-btn-icon mr-4 mank-btn-icon-md"
            color="primary">
            <Icon name="edit" variant="solid" color="default" size="16"/>
          </v-btn>

          <v-btn
            color="primary"
            size="large"
            class="start-call-btn mank-btn"
            @click="testAgent"
            elevation="0">
            <Icon name="start-call" variant="solid" color="onPrimary" class="mr-4"/>
            Test Agent
          </v-btn>
        </div>
      </div>

      <!-- Content -->
      <div class="main-content">
        <!-- Agent Info Table -->
        <div class="agent-info-table">
          <div class="info-row">
            <div class="info-column">
              <div class="info-label">
                Agent ID
              </div>
              <div class="info-value-with-icon">
                <span class="info-value-text">{{ agent.id }}</span>
                <v-btn
                  color="grey-lighten-2"
                  variant="flat"
                  size="x-small"
                  elevation="0"
                  icon="mdi-content-copy"
                  @click="copyAgentId"/>
              </div>
            </div>

            <div class="info-column">
              <div class="info-label">
                Date Created
              </div>
              <div class="info-value-text">
                {{ formatDate(agent.createdAt) }}
              </div>
            </div>

            <div class="info-column">
              <div class="info-label">
                Calls
              </div>
              <div class="info-value-text">
                {{ agent.callCount || 0 }}
              </div>
            </div>
          </div>
        </div>

        <!-- Tabs Content -->
        <div class="tabs-content">
          <!-- Tabs Area -->
          <div class="tabs-area mank-tabs-strip">
            <div class="tabs-container">
              <v-btn
                :color="activeTab === 'details' ? 'primary' : 'grey-lighten-2'"
                :variant="activeTab === 'details' ? 'flat' : 'text'"
                size="small"
                elevation="0"
                @click="activeTab = 'details'"
                class="tab-button"
                :class="{ 'active-tab': activeTab === 'details' }">
                Details
              </v-btn>

              <v-btn
                :color="activeTab === 'callHistory' ? 'primary' : 'grey-lighten-2'"
                :variant="activeTab === 'callHistory' ? 'flat' : 'text'"
                size="small"
                elevation="0"
                @click="activeTab = 'callHistory'"
                class="tab-button"
                :class="{ 'active-tab': activeTab === 'callHistory' }">
                Call History
              </v-btn>
            </div>
          </div>

          <!-- Tab Content -->
          <div class="tab-content" v-if="activeTab === 'details'">
            <!-- General Accordion -->
            <div class="accordion-section general-accordion">
              <div class="accordion-header general-header" @click="toggleAccordion('general')">
                <h3 class="general-title">
                  General
                </h3>
                <v-btn
                  color="grey-lighten-2"
                  variant="text"
                  size="x-small"
                  elevation="0"
                  :icon="accordions.general ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                  style="opacity: 0.6;"/>
              </div>

              <div class="accordion-content general-content" v-if="accordions.general">
                <div class="general-rows">
                  <div class="general-row">
                    <div class="general-label">
                      Agent Model
                    </div>
                    <div class="general-value">
                      {{ agent.model?.name || 'N/A' }}
                    </div>
                  </div>

                  <div class="general-row">
                    <div class="general-label">
                      Voice
                    </div>
                    <div class="general-value">
                      {{ agent.voice?.id || 'N/A' }}
                    </div>
                  </div>

                  <div class="general-row">
                    <div class="general-label">
                      Available Tools
                    </div>
                    <div class="general-value tools-value">
                      <ul v-if="agent.tools && agent.tools.length">
                        <li v-for="tool in agent.tools" :key="tool.id" class="tool-line">
                          {{ tool.name }}
                        </li>
                      </ul>
                      <div v-else>
                        N/A
                      </div>
                    </div>
                  </div>

                  <div class="general-row">
                    <div class="general-label">
                      System Prompt
                    </div>
                    <div class="general-value">
                      {{ agent.systemPrompt || 'N/A' }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Call Settings Accordion -->
            <div class="accordion-section call-settings-accordion">
              <div class="accordion-header call-settings-header" @click="toggleAccordion('callSettings')">
                <h3 class="call-settings-title">
                  Call Settings
                </h3>
                <v-btn
                  color="grey-lighten-2"
                  variant="text"
                  size="x-small"
                  elevation="0"
                  :icon="accordions.callSettings ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                  style="opacity: 0.6;"/>
              </div>

              <div class="accordion-content call-settings-content" v-if="accordions.callSettings">
                <!-- First Row Group -->
                <div class="call-settings-row-group">
                  <div class="call-settings-row">
                    <div class="call-settings-label">
                      Initiator
                    </div>
                    <div class="call-settings-value">
                      {{ agent.initiator || 'Agent' }}
                    </div>
                  </div>

                  <div class="call-settings-row">
                    <div class="call-settings-label">
                      First Message
                    </div>
                    <div class="call-settings-value call-settings-long-text">
                      {{ agent.firstMessage || 'N/A' }}
                    </div>
                  </div>

                  <div class="call-settings-row">
                    <div class="call-settings-label">
                      Interruptible
                    </div>
                    <div class="call-settings-value-with-icon">
                      <v-icon
                        v-if="agent.interruptible"
                        color="success"
                        size="small"
                        class="mr-1">
                        mdi-check-circle
                      </v-icon>
                      <span class="call-settings-value">
                        {{ agent.interruptible ? 'Yes' : 'No' }}
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Second Row Group -->
                <div class="call-settings-row-group call-settings-second-group">
                  <div class="call-settings-row">
                    <div class="call-settings-label">
                      Max Duration
                    </div>
                    <div class="call-settings-value">
                      {{ formatDuration(agent.maxDuration) }}
                    </div>
                  </div>

                  <div class="call-settings-row mr-auto ml-0">
                    <div class="call-settings-label">
                      Time Exceed Message
                    </div>
                    <div class="call-settings-value call-settings-long-text">
                      {{ agent.timeExceedMessage || 'N/A' }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Inactivity Messages Accordion -->
            <div class="accordion-section inactivity-accordion">
              <div class="accordion-header inactivity-header" @click="toggleAccordion('inactivityMessages')">
                <h3 class="inactivity-title">
                  Inactivity Messages
                </h3>
                <v-btn
                  color="grey-lighten-2"
                  variant="text"
                  size="x-small"
                  elevation="0"
                  :icon="accordions.inactivityMessages ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                  style="opacity: 0.6;"/>
              </div>

              <div class="accordion-content inactivity-content" v-if="accordions.inactivityMessages">
                <div v-if="agent.inactivityMessages && agent.inactivityMessages.length">
                  <div
                    v-for="(message, index) in agent.inactivityMessages"
                    :key="index"
                    class="inactivity-message-rows">
                    <div class="inactivity-row">
                      <div class="inactivity-label">
                        Name
                      </div>
                      <div class="inactivity-value">
                        {{ message.name }}
                      </div>
                    </div>

                    <div class="inactivity-row">
                      <div class="inactivity-label">
                        Max Duration
                      </div>
                      <div class="inactivity-value">
                        {{ message.maxDuration }}s
                      </div>
                    </div>

                    <div class="inactivity-row">
                      <div class="inactivity-label">
                        End Behavior
                      </div>
                      <div class="inactivity-value">
                        {{ message.endBehavior }}
                      </div>
                    </div>

                    <div class="inactivity-row">
                      <div class="inactivity-label">
                        Message
                      </div>
                      <div class="inactivity-value inactivity-long-text">
                        {{ message.message }}
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else class="text-element-2">
                  No inactivity messages configured
                </div>
              </div>
            </div>
          </div>

          <!-- Call History Tab Content -->
          <div class="tab-content" v-if="activeTab === 'callHistory'">
            <div v-if="!hasCallHistory" class="text-center pa-8">
              <v-icon size="64" color="grey-lighten-2" class="mb-4">
                mdi-phone-off
              </v-icon>
              <h3 class="text-h6 text-bold mb-2">
                No Call History
              </h3>
              <p class="text-body-2 text--secondary">
                This agent hasn't made any calls yet.
              </p>
            </div>

            <div v-else class="call-history-table">
              <v-data-table
                :items-per-page="selectedItemsPerPage"
                :page="callHistoryPage"
                :headers="callHistoryHeaders"
                :items="callHistory"
                :search="callHistorySearch"
                :loading="callHistoryLoading"
                class="call-history-data-table"
                item-value="id"
                no-data-text="No call history found"
                loading-text="Loading call history..."
                items-per-page-text="Calls per page:"
                hide-default-footer
                show-current-page>
                <template v-slot:header.callId="{ header }">
                  {{ header.text.toUpperCase() }}
                </template>

                <!-- Call ID Column -->
                <template v-slot:item.callId="{ item }">
                  <div class="d-flex align-center">
                    <div class="text-element-2">
                      {{ item.callId }}
                    </div>
                  </div>
                </template>

                <!-- Status Column with Tag -->
                <template v-slot:item.status="{ item }">
                  <v-chip
                    :color="getStatusColor(item.status)"
                    size="small"
                    variant="outlined"
                    class="status-chip">
                    {{ item.status }}
                  </v-chip>
                </template>

                <!-- Start Time Column -->
                <template v-slot:item.startTime="{ item }">
                  <div class="text-element-2">
                    {{ formatDateTime(item.startTime) }}
                  </div>
                </template>

                <!-- End Time Column -->
                <template v-slot:item.endTime="{ item }">
                  <div class="text-element-2">
                    {{ formatDateTime(item.endTime) }}
                  </div>
                </template>

                <!-- Duration Column -->
                <template v-slot:item.duration="{ item }">
                  <div class="text-element-2">
                    {{ formatCallDuration(item.duration) }}
                  </div>
                </template>

                <!-- Tool Errors Column -->
                <template v-slot:item.toolErrors="{ item }">
                  <div class="text-element-2">
                    {{ item.toolErrors || 0 }}
                  </div>
                </template>

                <!-- Summary Column -->
                <template v-slot:item.summary="{ item }">
                  <div class="text-element-2 summary-text">
                    {{ item.summary || 'N/A' }}
                  </div>
                </template>
              </v-data-table>
              <div class="table-footer">
                <v-pagination
                  v-model="page"
                  :total-visible="6"
                  :length="totalPages"/>
                <div class="pagination-info ml-auto mr-0">
                  <span class="text-caption-1 text--secondary">
                    Showing
                  </span>
                  <v-select
                    class="items-per-page-select"
                    :items="itemsPerPage"
                    v-model="selectedItemsPerPage"
                    hide-details
                    outlined>
                    <template v-slot:append>
                      <Icon name="expand-down" variant="solid" color="muted" size="16"/>
                    </template>
                  </v-select>
                  <span class="text-caption-1 text--secondary">
                    / {{ totalItems }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <DeleteConfirmationModal
      :visible="showDeleteDialog"
      @cancel="cancelDelete"
      @confirm="confirmDelete"/>
  </div>
</template>

<script>
import DeleteConfirmationModal from '../components/DeleteConfirmationModal.vue';
import AgentService from '../services/AgentService';

export default {
  name: 'AgentDetail',
  components: { DeleteConfirmationModal },
  data() {
    return {
      selectedItemsPerPage: 5,
      itemsPerPage: [5, 10, 20, 50, 100],
      activeTab: 'details',
      showDeleteDialog: false,
      accordions: {
        general: true,
        callSettings: true,
        inactivityMessages: true,
      },
      // Call History Data
      callHistorySearch: '',
      callHistoryLoading: false,
      callHistoryPage: 1,
      totalItems: 10,
      callHistoryHeaders: [
        {
          text: 'Call ID',
          value: 'callId',
          sortable: false,
        },
        {
          text: 'Start Time',
          value: 'startTime',
          sortable: false,
        },
        {
          text: 'End Time',
          value: 'endTime',
          sortable: false,
        },
        {
          text: 'Duration',
          value: 'duration',
          sortable: false,
        },
        {
          text: 'Billed Duration',
          value: 'billedDuration',
          sortable: false,
        },
        {
          text: 'Tool Errors',
          value: 'toolErrors',
          sortable: false,
        },
        {
          text: 'Summary',
          value: 'summary',
          sortable: false,
        },
      ],
      callHistory: [
        // Mock call history data
        {
          id: 1,
          callId: '1234-567-89',
          status: 'Ended',
          startTime: new Date('2025-01-07T14:19:00'),
          endTime: new Date('2025-01-07T14:22:08'),
          duration: 188, // seconds
          toolErrors: 3,
          summary: 'Customer inquiry about product features and pricing.',
        },
        {
          id: 2,
          callId: '1234-567-90',
          status: 'Canceled',
          startTime: new Date('2025-01-07T13:45:00'),
          endTime: new Date('2025-01-07T13:47:32'),
          duration: 152, // seconds
          toolErrors: 1,
          summary: 'Support request for technical issue resolution.',
        },
        {
          id: 3,
          callId: '1234-567-91',
          status: 'No Answer',
          startTime: new Date('2025-01-07T12:30:00'),
          endTime: null,
          duration: 0,
          toolErrors: 0,
          summary: 'Call was not answered by the customer.',
        },
      ],
      // Mock agent data - will be replaced with real data from API
      agent: {
        id: 'agt_1234567890',
        name: 'Customer Support Agent',
        createdAt: new Date('2024-01-15'),
        callCount: 3,
        model: {
          name: 'fixie-ai/mank',
          provider: 'fixie-ai',
        },
        voice: {
          id: 'feccf00b-417e-4e7a-9f89-62f537280334',
          name: 'Professional Voice',
        },
        tools: [
          { id: 1, name: 'leaveVoicemail' },
          { id: 2, name: 'playDtmfSounds' },
          { id: 3, name: 'queryCorpus' },
        ],
        systemPrompt: 'You are a professional customer support agent. Help users with their inquiries in a friendly and efficient manner.',
        initiator: 'Agent',
        firstMessage: 'Hello! How can I help you today?',
        interruptible: true,
        maxDuration: 480, // 8 minutes in seconds
        timeExceedMessage: 'I apologize, but our call time has exceeded the limit. Please call back if you need further assistance.',
        inactivityMessages: [
          {
            name: 'Message 1',
            maxDuration: 2,
            endBehavior: 'Hangup',
            message: 'Hello? Are you still there? Please let me know how I can help you.',
          },
          {
            name: 'Message 2',
            maxDuration: 2,
            endBehavior: 'Hangup',
            message: 'I haven\'t heard from you in a while. I\'ll end this call now. Please call back if you need assistance.',
          },
        ],
      },
    };
  },
  computed: {
    agentId() {
      return this.$route.params.id;
    },
    hasCallHistory() {
      return this.callHistory && this.callHistory.length > 0;
    },
  },
  methods: {
    goBack() {
      this.$router.push({ name: 'Agents' });
    },
    copyAgentId() {
      navigator.clipboard.writeText(this.agent.id);
      // TODO: Show success notification
      console.log('Agent ID copied to clipboard');
    },
    formatDate(date) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleDateString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    },
    formatDuration(seconds) {
      if (!seconds) return 'N/A';
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes} min ${remainingSeconds} s`;
    },
    formatDateTime(date) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    },
    formatCallDuration(seconds) {
      if (!seconds || seconds === 0) return '00:00:00';
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const remainingSeconds = seconds % 60;
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    },
    getStatusColor(status) {
      switch (status) {
      case 'Ended':
        return 'success';
      case 'Canceled':
        return 'warning';
      case 'No Answer':
        return 'error';
      case 'Connection Error':
        return 'error';
      default:
        return 'grey';
      }
    },
    toggleAccordion(section) {
      this.accordions[section] = !this.accordions[section];
    },
    deleteAgent() {
      this.showDeleteDialog = true;
    },
    editAgent() {
      // TODO: Navigate to edit page
      console.log('Edit agent:', this.agent.id);
    },
    testAgent() {
      // TODO: Implement test functionality
      console.log('Test agent:', this.agent.id);
      this.$router.push({ name: 'TestAgent', params: { id: this.agent.id } });
    },
    cancelDelete() {
      this.showDeleteDialog = false;
    },
    /**
     * Swagger: DELETE /agent/{id}
     */
    async confirmDelete() {
      const id = this.agent?.id;
      if (!id) {
        this.showDeleteDialog = false;
        return;
      }
      try {
        const response = await AgentService.deleteAgent(id);
        if (response && response.success) {
          this.$store.dispatch('ui/showNotification', {
            type: 'success',
            title: 'Success',
            message: `${this.agent.name} agenti başarıyla silindi.`,
          });
          this.showDeleteDialog = false;
          this.$router.push({ name: 'Agents' });
        } else {
          this.$store.dispatch('ui/showNotification', {
            type: 'error',
            title: 'Error',
            message: response?.message || 'Agent silinirken bir hata oluştu.',
          });
        }
      } catch (error) {
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          title: 'Error',
          message: error.message || 'Agent silinirken bir hata oluştu.',
        });
      } finally {
        this.showDeleteDialog = false;
      }
    },
  },
  mounted() {
    // TODO: Load agent data based on route params
    console.log('Loading agent with ID:', this.agentId);
  },
};
</script>

<style lang="scss" scoped>
@import '../styles/variables.scss';
@import '../styles/colors.scss';

.left-section {
  display: flex;
  flex-direction: row;
  align-items: center;
}

.right-section {
  display: flex;
  flex-direction: row;
  gap: 8px;
}

.agent-info-table {
  padding: 24px;

  .info-row {
    display: flex;
    flex-direction: row;
    gap: 24px;
  }

  .info-column {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

    .info-label {
    color: #93A0AF;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-weight: 400;
    font-size: $body-3-size;
    line-height: 1.2;
  }

  .info-value-text {
    color: #171D22;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-weight: 600;
    font-size: $body-3-size;
    line-height: 1.2;
  }

  .info-value-with-icon {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
  }
}

.tabs-content {
  display: flex;
  flex-direction: column;
  flex: 1;
}

.tab-content {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.accordion-section {
  border: 1px solid $light-border;
  border-radius: $card-border-radius;
  overflow: hidden;
}

.accordion-header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  border-bottom: 1px solid $light-border;
  cursor: pointer;
  background-color: $light-surface-1;
}

.accordion-content {
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.detail-row {
  display: flex;
  flex-direction: column;
  gap: 4px;

  &:not(:last-child) {
    margin-bottom: 12px;
  }
}

.detail-label {
  color: $light-foreground-2;
}

.detail-value {
  color: $light-foreground-1;

  .tool-item {
    margin-bottom: 4px;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.detail-value-with-icon {
  display: flex;
  flex-direction: row;
  align-items: center;
  color: $light-foreground-1;
}

.inactivity-message-group {
  padding: 12px;
  border: 1px solid $light-border;
  border-radius: 8px;
  background-color: $light-surface-2;

  &:not(:last-child) {
    margin-bottom: 12px;
  }
}

// General Accordion Specific Styles
.general-accordion {
  border: 1px solid #D3DAE0;
  border-radius: 12px;
  overflow: hidden;
}

.general-header {
  background-color: #F7F8FB;
  border-bottom: 1px solid #D3DAE0;
  padding: 8px 12px;

  &:hover {
    background-color: #F0F2F5;
  }
}

.general-title {
  color: #171D22;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 700;
  font-size: $body-2-size;
  line-height: 1.4;
  margin: 0;
}

.general-content {
  background-color: #FFFFFF;
  padding: 12px;
}

.general-rows {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 24px;
}

.general-row {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  flex: 1;

  &:first-child {
    min-height: 48px;
  }
}

.general-label {
  color: #93A0AF;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 400;
  font-size: $body-3-size;
  line-height: 1.2;
}

.general-value {
  color: #171D22;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 600;
  font-size: $body-3-size;
  line-height: 1.2;
  min-height: 24px;
  display: flex;
  align-items: center;
}

.tools-value {
  line-height: 1.6;

  .tool-line {
    margin-bottom: 0;
    line-height: 1.6;
  }
}

// Call Settings Accordion Specific Styles
.call-settings-accordion {
  border: 1px solid #D3DAE0;
  border-radius: 12px;
  overflow: hidden;
}

.call-settings-header {
  background-color: #F7F8FB;
  border-bottom: 1px solid #D3DAE0;
  padding: 8px 12px;

  &:hover {
    background-color: #F0F2F5;
  }
}

.call-settings-title {
  color: #171D22;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 700;
  font-size: $body-2-size;
  line-height: 1.4;
  margin: 0;
}

.call-settings-content {
  background-color: #FFFFFF;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.call-settings-row-group {
  display: flex;
  flex-direction: row;
  justify-content: stretch;
  align-items: stretch;
}

.call-settings-second-group {
  height: 72px;
}

.call-settings-row {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  flex: 1;
  max-width: 33%;

  &:first-child {
    min-height: 48px;
  }
}

.call-settings-label {
  color: #93A0AF;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 400;
  font-size: $body-3-size;
  line-height: 1.2;
}

.call-settings-value {
  color: #171D22;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 600;
  font-size: $body-3-size;
  line-height: 1.2;
  min-height: 24px;
  display: flex;
  align-items: center;
}

.call-settings-long-text {
  width: 261px;
  line-height: 1.2;
  overflow-wrap: break-word;
}

.call-settings-value-with-icon {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  min-height: 24px;
}

// Inactivity Messages Accordion Specific Styles
.inactivity-accordion {
  border: 1px solid #D3DAE0;
  border-radius: 12px;
  overflow: hidden;
}

.inactivity-header {
  background-color: #F7F8FB;
  border-bottom: 1px solid #D3DAE0;
  padding: 8px 12px;

  &:hover {
    background-color: #F0F2F5;
  }
}

.inactivity-title {
  color: #171D22;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 700;
  font-size: $body-2-size;
  line-height: 1.4;
  margin: 0;
}

.inactivity-content {
  background-color: #FFFFFF;
  padding: 0;
}

.inactivity-message-rows {
  display: flex;
  flex-direction: row;
  justify-content: stretch;
  align-items: stretch;
  gap: 24px;
  padding: 12px;
  border-bottom: 1px solid #D3DAE0;

  &:last-child {
    border-bottom: none;
  }
}

.inactivity-row {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  flex: 1;

  &:first-child {
    min-height: 48px;
  }

  &:last-child {
    min-height: auto;
  }
}

.inactivity-label {
  color: #93A0AF;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 400;
  font-size: $body-3-size;
  line-height: 1.2;
}

.inactivity-value {
  color: #171D22;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 600;
  font-size: $body-3-size;
  line-height: 1.2;
  min-height: 24px;
  display: flex;
  align-items: center;
}

.inactivity-long-text {
  width: 261px;
  line-height: 1.2;
  overflow-wrap: break-word;
}

// Responsive design
@media (max-width: map-get($grid-breakpoints, md)) {
  .page-header {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }

  .left-section,
  .right-section {
    justify-content: center;
  }

  .info-row {
    flex-direction: column;
  }

  .mank-tabs-strip .tabs-container {
    justify-content: center;
  }
}

// Call History Table Styles
.call-history-table {
  background-color: $light-surface-1;
  border-radius: $card-border-radius;
  overflow: hidden;
}

.call-history-data-table {

  .status-chip {
    font-weight: 600;
    font-size: $caption-1-size;
  }

  .summary-text {
    max-width: 160px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
::v-deep .v-pagination .v-pagination__navigation{
    box-shadow: none;
    border:none;
}
::v-deep .v-pagination .v-pagination__item{
    box-shadow: none;
    border:none;
    font-weight: 600;
    width: 24px;
    height: 24px;
    min-height: 24px;
    max-height: 24px;
    width: 24px;
    height: 24px;
    min-width: 24px;
}
::v-deep .v-pagination .v-pagination__item--active{
    background-color: transparent !important;
    color: $light-primary;
    border:1px solid $light-primary-neutral;
}
::v-deep .v-data-table-header{
  background-color: $light-surface-1;
}
::v-deep .v-data-table-header tr th{
  color: $light-foreground-3 !important;
  font-weight: 600;
  font-size: $body-3-size !important;
  text-transform: capitalize;
}

.table-footer {
  background: $light-surface-2;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-around;
  width: 100%;
  border-top: 1px solid $light-border;
  height: 48px ;
}
.pagination-info {
  font-size: $caption-1-size;
  color: $light-foreground-2;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  justify-content: center;
}

.items-per-page-select {
  width: 48px;
  height: 24px;
  min-height: 24px;
  max-height: 24px;
}
</style>
